import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import {
  FormProvider,
  useForm,
  useWatch,
} from 'react-hook-form'
import type { Path, Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DebtsStep } from '../components/profile/DebtsStep'
import { EstateStep } from '../components/profile/EstateStep'
import { IncomeStep } from '../components/profile/IncomeStep'
import { InsuranceStep } from '../components/profile/InsuranceStep'
import { PersonalStep } from '../components/profile/PersonalStep'
import { RetirementStep } from '../components/profile/RetirementStep'
import { ReviewStep } from '../components/profile/ReviewStep'
import { SavingsStep } from '../components/profile/SavingsStep'
import {
  PROFILE_STEPS,
  fnaFormSchema,
  type FnaFormValues,
} from '../contracts/fnaFormSchema'
import { useProgressionContext } from '../context/ProgressionContext'
import { completeAssessment } from '../services/api'
import type { FnaFormData } from '../types'
import '../styles/progression.css'

const PROFILE_STEP_NUMBER = 2

/** Empty defaults so inputs start controlled (no React warnings). */
const EMPTY_DEFAULTS: Partial<FnaFormValues> = {
  name: '',
  gender: undefined,
  withSpouse: undefined,
  spouseName: '',
  spouseGender: undefined,
  occupation: '',
  spouseOccupation: '',
  otherIncomeSource: '',
  spouseOtherIncomeSource: '',
  retirementIncome: undefined,
  additionalRetireIncome: undefined,
  sourceOfIncome: '',
  previousSavings: undefined,
  rolledOver: undefined,
  lifeInsurance: undefined,
  estateToHeirs: undefined,
  will: undefined,
  hasDebts: undefined,
  children: undefined,
  debts: [],
}

type CleanableField = keyof FnaFormValues

const SPOUSE_FIELDS: CleanableField[] = [
  'spouseName',
  'spouseGender',
  'spouseAge',
  'spouseOccupation',
  'spouseAnnualIncome',
  'spouseOtherMonthlyIncome',
  'spouseOtherIncomeSource',
  'clientRetirement',
  'spouseRetirement',
  'jointRetirement',
  'spouseMonthlyContribution',
  'spouseEmployerContribution',
  'clientNonRetirementSavings',
  'spouseNonRetirementSavings',
  'jointNonRetirementSavings',
  'spouseMonthlySavings',
  'jointMonthlySavings',
]

const CLEANED_FIELD_NAMES: CleanableField[] = [
  ...SPOUSE_FIELDS,
  'numChildren',
  'otherIncomeSource',
  'spouseOtherIncomeSource',
  'sourceOfIncome',
  'monthlyAmount',
  'rolledOver',
  'insuredAmount',
  'amountEstate',
  'debts',
  'monthlyRetirementContribution',
]

const toFiniteNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const isPositiveAmount = (value: unknown): boolean => {
  const parsed = toFiniteNumber(value)
  return parsed !== undefined && parsed > 0
}

const valuesAreEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a ?? []) === JSON.stringify(b ?? [])
  }
  return false
}

function clearFields(values: Partial<FnaFormValues>, fields: ReadonlyArray<CleanableField>): void {
  for (const field of fields) {
    delete values[field]
  }
}

function syncDerivedMonthlyRetirementContribution(values: Partial<FnaFormValues>): void {
  const contributions: number[] = []
  const clientContribution = toFiniteNumber(values.clientMonthlyContribution)
  const spouseContribution = toFiniteNumber(values.spouseMonthlyContribution)

  if (clientContribution !== undefined) contributions.push(clientContribution)
  if (values.withSpouse === 'yes' && spouseContribution !== undefined) {
    contributions.push(spouseContribution)
  }

  if (contributions.length === 0) {
    delete values.monthlyRetirementContribution
    return
  }

  const total = contributions.reduce((sum, value) => sum + value, 0)
  values.monthlyRetirementContribution = Number.isInteger(total) ? total : total
}

function normalizeConditionalValues(values: Partial<FnaFormValues>): Partial<FnaFormValues> {
  const next: Partial<FnaFormValues> = { ...values }

  if (next.withSpouse !== 'yes') {
    clearFields(next, SPOUSE_FIELDS)
  } else if (!isPositiveAmount(next.spouseOtherMonthlyIncome)) {
    delete next.spouseOtherIncomeSource
  }

  if (next.children !== 'yes') clearFields(next, ['numChildren'])
  if (!isPositiveAmount(next.otherMonthlyIncome)) clearFields(next, ['otherIncomeSource'])
  if (next.additionalRetireIncome !== 'yes') clearFields(next, ['sourceOfIncome', 'monthlyAmount'])
  if (next.previousSavings !== 'yes') clearFields(next, ['rolledOver'])
  if (next.lifeInsurance !== 'yes') clearFields(next, ['insuredAmount'])
  if (next.estateToHeirs !== 'yes') clearFields(next, ['amountEstate'])
  if (next.hasDebts !== 'yes') next.debts = []

  syncDerivedMonthlyRetirementContribution(next)
  return next
}

function buildProfilePayload(values: FnaFormValues): FnaFormData {
  const normalized = normalizeConditionalValues(values)
  const payload: FnaFormData = {}

  for (const [key, value] of Object.entries(normalized)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && value.trim() === '') continue
    payload[key] = value
  }

  return payload
}

/**
 * Loads form data from the progression context or localStorage so that users
 * who began the assessment elsewhere (the hidden conversation path, an
 * earlier session) see their prior answers populated.
 *
 * @param contextFormData - Form data already in the progression context
 * @returns The initial values to pass to RHF
 */
function loadInitialValues(contextFormData: unknown): Partial<FnaFormValues> {
  if (contextFormData && typeof contextFormData === 'object' && Object.keys(contextFormData as object).length > 0) {
    return { ...EMPTY_DEFAULTS, ...(contextFormData as Partial<FnaFormValues>) }
  }
  try {
    const stored = localStorage.getItem('form_data')
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<FnaFormValues>
      return { ...EMPTY_DEFAULTS, ...parsed }
    }
  } catch (err) {
    console.error({
      message: 'Error loading form_data from localStorage',
      error: err instanceof Error ? err.message : String(err),
      context: { action: 'ProfilePage.loadInitialValues' },
    })
  }
  return { ...EMPTY_DEFAULTS }
}

/**
 * Extracts `field_errors` ("fieldName: msg") from the backend response and
 * routes each onto RHF via `setError`. Falls back to a banner if nothing
 * maps cleanly.
 *
 * @param err - The thrown error from `completeAssessment`
 * @param setError - RHF's setError function
 * @returns A banner string when no per-field error could be applied
 */
function applyBackendFieldErrors(
  err: unknown,
  setError: (name: Path<FnaFormValues>, error: { type: string; message: string }) => void,
): string {
  let banner = 'Failed to save your assessment. Please try again.'
  if (err instanceof AxiosError && err.response?.data) {
    const data = err.response.data as Record<string, unknown>
    const fieldErrors = Array.isArray(data.field_errors) ? (data.field_errors as string[]) : []
    let appliedAny = false
    for (const entry of fieldErrors) {
      const colonIdx = entry.indexOf(':')
      if (colonIdx <= 0) continue
      const fieldName = entry.slice(0, colonIdx).trim() as Path<FnaFormValues>
      const message = entry.slice(colonIdx + 1).trim() || 'Invalid value'
      try {
        setError(fieldName, { type: 'server', message })
        appliedAny = true
      } catch {
        // Field name didn't resolve - fall through to banner.
      }
    }
    if (!appliedAny) {
      if (typeof data.error === 'string') {
        banner = `Assessment save failed: ${data.error}`
      } else if (fieldErrors.length > 0) {
        banner = `Validation errors: ${fieldErrors.join(', ')}`
      }
    } else {
      banner = 'Some fields need attention before saving.'
    }
  } else if (err instanceof Error) {
    banner = `Assessment save failed: ${err.message}`
  }
  return banner
}

/**
 * Multi-step Financial Needs Assessment profile form.
 *
 * Replaces the LLM conversation in the primary onboarding flow with
 * deterministic, inline-validated fields. The legacy conversation path at
 * `/conversation` -> `/datacorrection` is intentionally left in place and
 * still reachable by direct URL.
 *
 * @returns JSX.Element The profile form page
 */
const ProfilePage: React.FC = () => {
  const navigate = useNavigate()
  const { formData, setFormData, completeStep } = useProgressionContext()
  const [stepIdx, setStepIdx] = useState(0)
  const [bannerError, setBannerError] = useState<string | null>(null)

  // Hydrate from context/localStorage exactly once. RHF's defaultValues are
  // captured on mount; later changes to formData should not blow away typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only
  const initialValues = useMemo(() => loadInitialValues(formData), [])

  // The schema uses z.preprocess + z.coerce so the resolver's input type is
  // looser (`unknown` for numeric fields) than the validated output type.
  // We're intentionally working with the validated shape across the form, so
  // cast the resolver into that shape. The runtime behavior is unaffected.
  const methods = useForm<FnaFormValues>({
    resolver: zodResolver(fnaFormSchema) as unknown as Resolver<FnaFormValues>,
    defaultValues: initialValues,
    mode: 'onBlur',
    shouldUnregister: false,
  })

  const step = PROFILE_STEPS[stepIdx]
  const isLastStep = stepIdx === PROFILE_STEPS.length - 1

  // Keep stepIdx in range if PROFILE_STEPS ever changes at HMR time.
  useEffect(() => {
    if (stepIdx >= PROFILE_STEPS.length) setStepIdx(PROFILE_STEPS.length - 1)
  }, [stepIdx])

  const valuesForCleanup = useWatch({ control: methods.control }) as Partial<FnaFormValues>

  useEffect(() => {
    const cleaned = normalizeConditionalValues(valuesForCleanup)

    for (const field of CLEANED_FIELD_NAMES) {
      const nextValue = cleaned[field]
      const currentValue = valuesForCleanup[field]

      if (!valuesAreEqual(currentValue, nextValue)) {
        methods.setValue(field as Path<FnaFormValues>, nextValue as never, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        })
        methods.clearErrors(field as Path<FnaFormValues>)
      }
    }
  }, [methods, valuesForCleanup])

  const goNext = async () => {
    setBannerError(null)
    const fieldsToValidate = step.fields as ReadonlyArray<Path<FnaFormValues>>
    const isStepValid =
      fieldsToValidate.length === 0
        ? true
        : await methods.trigger(fieldsToValidate as Path<FnaFormValues>[], { shouldFocus: true })
    if (!isStepValid) return
    setStepIdx((idx) => Math.min(idx + 1, PROFILE_STEPS.length - 1))
  }

  const goBack = () => {
    setBannerError(null)
    setStepIdx((idx) => Math.max(idx - 1, 0))
  }

  const onSubmit = methods.handleSubmit(async (values) => {
    setBannerError(null)
    try {
      const payload = buildProfilePayload(values)
      setFormData(payload)
      const completion = await completeAssessment(payload)
      try {
        if (completion.summary) {
          localStorage.setItem('results_summary', JSON.stringify(completion.summary))
        } else {
          localStorage.removeItem('results_summary')
        }
      } catch (storageError) {
        console.error({
          message: 'Error persisting results_summary after assessment completion',
          error: storageError instanceof Error ? storageError.message : String(storageError),
          context: { action: 'ProfilePage.onSubmit.cacheSummary' },
        })
      }
      completeStep(PROFILE_STEP_NUMBER)
      navigate('/userquestionsgemini')
    } catch (err) {
      console.error({
        message: 'Error saving assessment data',
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        context: { action: 'ProfilePage.onSubmit' },
      })
      const banner = applyBackendFieldErrors(err, methods.setError)
      setBannerError(banner)
    }
  })

  return (
    <FormProvider {...methods}>
      <div className="form-page">
        <div className="form-header">
          <h1>Financial Needs Analysis</h1>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((stepIdx + 1) / PROFILE_STEPS.length) * 100}%` }}
            />
          </div>
          <p>
            Step {stepIdx + 1} of {PROFILE_STEPS.length}: {step.label}
          </p>
        </div>

        {bannerError && (
          <div className="error-banner">
            <span className="error-icon">!</span>
            <p>{bannerError}</p>
            <button onClick={() => setBannerError(null)} className="close-error">&times;</button>
          </div>
        )}

        <form
          onSubmit={(e) => {
            if (!isLastStep) {
              e.preventDefault()
              void goNext()
              return
            }
            void onSubmit(e)
          }}
          noValidate
          className="form-container"
        >
          <div className="form-section">
            {step.id === 'personal' && <PersonalStep />}
            {step.id === 'income' && <IncomeStep />}
            {step.id === 'retirement' && <RetirementStep />}
            {step.id === 'savings' && <SavingsStep />}
            {step.id === 'insurance' && <InsuranceStep />}
            {step.id === 'estate' && <EstateStep />}
            {step.id === 'debts' && <DebtsStep />}
            {step.id === 'review' && <ReviewStep />}
          </div>

          <div className="form-navigation">
            {stepIdx > 0 ? (
              <button type="button" onClick={goBack} className="nav-btn prev-btn">
                Previous
              </button>
            ) : (
              <Link to="/accounts" className="nav-btn prev-btn">
                Back to Accounts
              </Link>
            )}

            {!isLastStep && (
              <button type="submit" className="nav-btn next-btn">
                Next
              </button>
            )}

            {isLastStep && (
              <button
                type="submit"
                className="nav-btn submit-btn"
                disabled={methods.formState.isSubmitting}
              >
                {methods.formState.isSubmitting ? 'Saving…' : 'Confirm & Continue'}
              </button>
            )}
          </div>
        </form>
      </div>
    </FormProvider>
  )
}

export default ProfilePage
